/**
 * CR097: Admin Live Preview Page
 * CR412: Restructured with Preview/Diagnostics tabs, sticky toolbar, compact layout
 * 
 * Preview deals from different sources:
 *   - Staging: cr075_staging_deals_deduped for a selected batch run
 *   - Admin (Prod): cr075_production_deals unfiltered
 *   - Live (Public): cr075_production_deals filtered by site_state pointer
 */

import { useState, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useSiteState } from "@/hooks/useSiteState";
import { useAdminPreviewDeals, type PreviewMode, type PreviewDeal } from "@/hooks/useAdminPreviewDeals";
import { useActiveLayout, type LayoutEnvironment } from "@/hooks/useActiveLayout";
import { LivePreviewToolbar } from "@/components/admin/live-preview/LivePreviewToolbar";
import { DiagnosticsTabContent } from "@/components/admin/live-preview/DiagnosticsTabContent";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { DealTypeFilter } from "@/components/deals/DealTypeFilter";
import { DealGridWithDetails } from "@/components/deals/DealGridWithDetails";
import { DealCardUXProvider } from "@/contexts/DealCardUXContext";
import { DEFAULT_DEAL_CARD_UX_CONFIG } from "@/lib/layout-studio/dealCardUXTypes";
import { FoldLineOverlay } from "@/components/deals/FoldLineOverlay";
import { useFoldLineSettings } from "@/hooks/useFoldLineSettings";
import type { FoldLineHeight } from "@/components/deals/FoldLineOverlay";
import { sortDeals } from "@/lib/types/deals";
import { 
  RefreshCw, 
  AlertTriangle,
  Database,
  Globe,
  Shield,
  CheckCircle2,
  XCircle,
  Clock,
  ThumbsUp,
  ThumbsDown,
  RotateCcw,
  Upload,
  ArrowRight,
  Package,
  LayoutGrid,
  Layers,
  Eye,
  History,
} from "lucide-react";
import { type DealCategory, inferDealCategory } from "@/lib/types/deals";
import { toast } from "sonner";
import { getMerchant, getAllMerchants } from "@/lib/merchantIndex";
import { PublishedDealCardConfigProvider, usePublishedDealCardConfigContext } from "@/contexts/PublishedDealCardConfigContext";
import { EnvironmentSelector } from "@/components/admin/deal-cards/EnvironmentSelector";
import type { PublishEnvironment } from "@/hooks/usePromoteDealCardConfig";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// ======= Category lookup =======
function normalizeToMerchantKey(merchantName: string): string {
  return merchantName
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

function getDealCategory(deal: PreviewDeal): string | null {
  if (!deal.merchant) return null;
  const merchantName = deal.merchant;
  const normalizedKey = normalizeToMerchantKey(merchantName);
  
  const merchant = getMerchant(normalizedKey);
  if (merchant?.category) return merchant.category;
  
  const allMerchants = getAllMerchants();
  for (const m of allMerchants) {
    if (m.display_name.toLowerCase() === merchantName.toLowerCase()) {
      if (m.category) return m.category;
    }
  }
  
  for (const m of allMerchants) {
    const displayLower = m.display_name.toLowerCase();
    const nameLower = merchantName.toLowerCase();
    if (displayLower.includes(nameLower) || nameLower.includes(displayLower)) {
      if (m.category) return m.category;
    }
  }
  
  return null;
}

// ======= Review status badge =======
function ReviewStatusBadge({ status }: { status: string | null }) {
  if (!status) {
    return (
      <Badge variant="outline" className="text-xs">
        <Clock className="w-3 h-3 mr-1" />
        Pending
      </Badge>
    );
  }
  if (status === "ACCEPTED") {
    return (
      <Badge className="bg-green-500/20 text-green-700 border-green-500/30 text-xs">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Accepted
      </Badge>
    );
  }
  if (status === "REJECTED") {
    return (
      <Badge className="bg-red-500/20 text-red-700 border-red-500/30 text-xs">
        <XCircle className="w-3 h-3 mr-1" />
        Rejected
      </Badge>
    );
  }
  return <Badge variant="outline" className="text-xs">{status}</Badge>;
}

// ======= Staging Grid with Review Controls =======
function LivePreviewStagingGrid({
  deals,
  onReview,
}: {
  deals: PreviewDeal[];
  onReview: (dealId: string, decision: "ACCEPTED" | "REJECTED" | "PENDING") => void;
}) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {deals.map((deal, index) => (
        <div key={`${deal.id}-${index}`} className="relative">
          <div className="absolute -top-2 -right-2 z-10 flex items-center gap-1 bg-background border rounded-lg p-1 shadow-sm">
            <ReviewStatusBadge status={deal.review_status} />
            <div className="flex gap-0.5 ml-1">
              <Button
                size="icon"
                variant={deal.review_status === "ACCEPTED" ? "default" : "ghost"}
                className="h-6 w-6"
                onClick={() => onReview(deal.id, "ACCEPTED")}
                title="Accept"
              >
                <ThumbsUp className="h-3 w-3" />
              </Button>
              <Button
                size="icon"
                variant={deal.review_status === "REJECTED" ? "destructive" : "ghost"}
                className="h-6 w-6"
                onClick={() => onReview(deal.id, "REJECTED")}
                title="Reject"
              >
                <ThumbsDown className="h-3 w-3" />
              </Button>
              {deal.review_status && deal.review_status !== "PENDING" && (
                <Button
                  size="icon"
                  variant="ghost"
                  className="h-6 w-6"
                  onClick={() => onReview(deal.id, "PENDING")}
                  title="Reset to Pending"
                >
                  <RotateCcw className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
          <DealGridWithDetails deals={[deal]} isAdmin={true} className="" />
        </div>
      ))}
    </div>
  );
}

export default function AdminLivePreview() {
  const { isAdmin } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const { 
    siteState, publishHistory, loading: stateLoading, refetch: refetchSiteState,
    promoteToAdmin, publishToLive, promoting, publishing,
  } = useSiteState();
  const previewDeals = useAdminPreviewDeals();
  
  // Fold line
  const { settings: foldSettings, setEnabled } = useFoldLineSettings();
  const foldHeight: FoldLineHeight = foldSettings.enabled ? "normal" : "off";
  const setFoldHeight = (h: FoldLineHeight) => setEnabled(h !== "off");

  // CR412: Page-level tab (preview | diagnostics)
  const pageTab = searchParams.get("tab") || "preview";
  const setPageTab = (tab: string) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", tab);
    setSearchParams(params, { replace: true });
  };

  const initialMode = (searchParams.get("mode") as PreviewMode) || "live";
  const initialBatchId = searchParams.get("batch_run_id") || "";
  
  const [currentMode, setCurrentMode] = useState<PreviewMode>(initialMode);
  const [selectedBatchId, setSelectedBatchId] = useState<string>(initialBatchId);
  
  // CR415: Deal card config environment (maps data mode to config env)
  const [dealCardConfigEnv, setDealCardConfigEnv] = useState<PublishEnvironment>(
    initialMode === "staging" ? "staging" : "production"
  );

  // Layout
  const layoutEnvironment: LayoutEnvironment = currentMode === "staging" ? "staging" : "publish";
  const activeLayout = useActiveLayout(layoutEnvironment);
  const dealCardUXConfig = useMemo(() => {
    if (activeLayout.layout?.dealCardUX) return activeLayout.layout.dealCardUX;
    return DEFAULT_DEAL_CARD_UX_CONFIG;
  }, [activeLayout.layout]);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [selectedDealTypes, setSelectedDealTypes] = useState<DealCategory[] | "All">("All");

  // Confirmation dialogs
  const [showPromoteDialog, setShowPromoteDialog] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);

  useEffect(() => { previewDeals.fetchBatchRuns(); }, []);

  useEffect(() => {
    if (currentMode === "staging") {
      if (selectedBatchId) previewDeals.fetchDeals("staging", selectedBatchId);
    } else {
      previewDeals.fetchDeals(currentMode);
    }
  }, [currentMode, selectedBatchId]);

  // Sync mode to URL (preserve tab)
  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set("mode", currentMode);
    if (currentMode === "staging" && selectedBatchId) {
      params.set("batch_run_id", selectedBatchId);
    } else {
      params.delete("batch_run_id");
    }
    setSearchParams(params, { replace: true });
  }, [currentMode, selectedBatchId]);

  // Filter deals
  const filteredDeals = useMemo((): PreviewDeal[] => {
    let deals: PreviewDeal[] = [...previewDeals.deals];
    if (selectedCategory !== "All") {
      deals = deals.filter((deal) => {
        const dc = getDealCategory(deal);
        return selectedCategory === "Unclassified" ? dc === null : dc === selectedCategory;
      });
    }
    if (selectedDealTypes !== "All") {
      deals = deals.filter((deal) => selectedDealTypes.includes(inferDealCategory(deal)));
    }
    if (searchQuery.trim().length >= 1) {
      const query = searchQuery.toLowerCase().trim();
      deals = deals.filter((deal) => {
        const brand = deal.purchase_gift_card_brand ?? deal.gift_card_brand;
        return brand?.toLowerCase().includes(query) ||
          deal.merchant.toLowerCase().includes(query) ||
          deal.bonus_merchant?.toLowerCase().includes(query) ||
          deal.headline_mechanic?.toLowerCase().includes(query);
      });
    }
    return sortDeals(deals) as PreviewDeal[];
  }, [previewDeals.deals, searchQuery, selectedCategory, selectedDealTypes]);

  const reviewCounts = useMemo(() => {
    if (currentMode !== "staging") return null;
    const accepted = previewDeals.deals.filter(d => d.review_status === "ACCEPTED").length;
    const rejected = previewDeals.deals.filter(d => d.review_status === "REJECTED").length;
    const pending = previewDeals.deals.filter(d => !d.review_status || d.review_status === "PENDING").length;
    return { accepted, rejected, pending };
  }, [previewDeals.deals, currentMode]);

  const endpointUrl = useMemo(() => {
    const base = `admin-preview-deals?mode=${currentMode}`;
    return currentMode === "staging" && selectedBatchId ? `${base}&batch_run_id=${selectedBatchId}` : base;
  }, [currentMode, selectedBatchId]);

  const handleModeChange = (newMode: string) => {
    setCurrentMode(newMode as PreviewMode);
    if (newMode === "staging" && !selectedBatchId && previewDeals.batchRuns.length > 0) {
      setSelectedBatchId(previewDeals.batchRuns[0].id);
    }
  };

  const handleRefresh = () => {
    refetchSiteState();
    activeLayout.refetch();
    if (currentMode === "staging") {
      previewDeals.fetchDeals("staging", selectedBatchId);
    } else {
      previewDeals.fetchDeals(currentMode);
    }
  };

  const handleReview = async (dealId: string, decision: "ACCEPTED" | "REJECTED" | "PENDING") => {
    if (!selectedBatchId) return;
    const deal = previewDeals.deals.find(d => d.id === dealId);
    const success = await previewDeals.submitReview(dealId, decision, selectedBatchId, deal?.dedupe_key);
    if (success) toast.success(`Deal marked as ${decision.toLowerCase()}`);
    else toast.error("Failed to update review status");
  };

  const handlePromoteToAdmin = async () => {
    if (!selectedBatchId) return;
    const result = await promoteToAdmin(selectedBatchId);
    if (result.success) {
      setShowPromoteDialog(false);
      previewDeals.fetchDeals("staging", selectedBatchId);
    }
  };

  const handlePublishToLive = async () => {
    const result = await publishToLive();
    if (result.success) {
      setShowPublishDialog(false);
      refetchSiteState();
    }
  };

  const acceptedDealCount = reviewCounts?.accepted ?? 0;
  const hasAdminSnapshot = !!siteState?.admin_snapshot_id;
  const adminDifferentFromLive = siteState?.admin_snapshot_id !== siteState?.active_snapshot_id;
  const canPromote = currentMode === "staging" && selectedBatchId && acceptedDealCount > 0;
  const canPublishToLive = currentMode === "admin" && hasAdminSnapshot && adminDifferentFromLive;
  const hasActiveSnapshot = !!siteState?.active_snapshot_id;

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="text-center py-12 text-muted-foreground">Admin access required</div>
      </AdminLayout>
    );
  }

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString("en-CA", { timeZone: "America/Toronto", dateStyle: "medium", timeStyle: "short" });
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    return `${diffDays}d ago`;
  };

  const recentPublishes = publishHistory.filter(h => 
    h.action === "PUBLISH" || h.action === "PROMOTE_TO_ADMIN" || h.action === "PUBLISH_TO_LIVE"
  ).slice(0, 5);

  return (
    <AdminLayout>
      <div className="space-y-0">
        {/* CR412: Page-level tabs */}
        <Tabs value={pageTab} onValueChange={setPageTab}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-3">
              <Eye className="h-5 w-5 text-primary" />
              <span className="text-lg font-semibold">Live Preview</span>
            </div>
            <TabsList className="h-8">
              <TabsTrigger value="preview" className="text-xs h-7 px-3">Preview</TabsTrigger>
              <TabsTrigger value="diagnostics" className="text-xs h-7 px-3">Diagnostics</TabsTrigger>
            </TabsList>
          </div>

          {/* ========== PREVIEW TAB ========== */}
          <TabsContent value="preview" className="mt-0 space-y-0">
            {/* Sticky toolbar */}
            <LivePreviewToolbar
              currentMode={currentMode}
              onModeChange={handleModeChange}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              selectedCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              deals={previewDeals.deals}
              foldHeight={foldHeight}
              onFoldHeightChange={setFoldHeight}
              loading={previewDeals.loading}
              stateLoading={stateLoading}
              onRefresh={handleRefresh}
              selectedBatchId={selectedBatchId}
              onBatchChange={(id) => setSelectedBatchId(id)}
              batchRuns={previewDeals.batchRuns}
            />

            {/* Compact environment banner (one-liner) */}
            <div className={`flex items-center gap-3 px-3 py-1.5 rounded text-xs mt-3 ${
              currentMode === "staging" ? "bg-amber-500/10 border border-amber-500/30 text-amber-700" :
              currentMode === "admin" ? "bg-purple-500/10 border border-purple-500/30 text-purple-700" :
              hasActiveSnapshot ? "bg-green-500/10 border border-green-500/30 text-green-700" : "bg-amber-500/10 border border-amber-500/30 text-amber-700"
            }`}>
              {currentMode === "staging" && (
                <>
                  <Layers className="h-3.5 w-3.5" />
                  <span className="font-medium">Previewing: Staging (Dedup)</span>
                  <span className="text-muted-foreground">• Batch: <span className="font-mono">{selectedBatchId?.slice(0, 12) || "None"}</span></span>
                </>
              )}
              {currentMode === "admin" && (
                <>
                  <Shield className="h-3.5 w-3.5" />
                  <span className="font-medium">Previewing: Admin (Prod)</span>
                  <span className="text-muted-foreground">• Unfiltered internal view</span>
                </>
              )}
              {currentMode === "live" && hasActiveSnapshot && (
                <>
                  <Globe className="h-3.5 w-3.5" />
                  <span className="font-medium">Previewing: Live (Public)</span>
                  <span className="text-muted-foreground">• Snapshot: <span className="font-mono">{siteState?.active_snapshot_id?.slice(0, 16)}</span></span>
                  <span className="text-muted-foreground">• Published: {formatDate(siteState?.published_at)}</span>
                </>
              )}
              {currentMode === "live" && !hasActiveSnapshot && (
                <>
                  <AlertTriangle className="h-3.5 w-3.5" />
                  <span className="font-medium">No active snapshot — public site shows zero deals</span>
                </>
              )}
            </div>

            {/* CR415: Deal Card Config Environment selector */}
            <div className="flex items-center gap-3 mt-2 px-3 py-1.5 rounded text-xs bg-muted/50 border border-border">
              <span className="text-muted-foreground font-medium">Card Config:</span>
              <EnvironmentSelector
                value={dealCardConfigEnv}
                onChange={setDealCardConfigEnv}
                compact
              />
            </div>

            {/* Collapsible Snapshot & Layout accordion */}
            <Accordion type="multiple" className="mt-3">
              <AccordionItem value="snapshot-layout" className="border rounded-lg">
                <AccordionTrigger className="px-4 py-2.5 text-sm hover:no-underline">
                  <div className="flex items-center gap-2">
                    <Package className="h-4 w-4 text-muted-foreground" />
                    <span>Snapshot & Layout</span>
                    <Badge variant="outline" className="text-[10px] h-4 ml-2">
                      {previewDeals.deals.length} deals
                    </Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  {/* Snapshot Status */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Layers className="h-3.5 w-3.5 text-amber-600" />
                        <span className="text-xs font-medium">Staging (Dedup)</span>
                      </div>
                      <div className="text-[11px] font-mono truncate">
                        {selectedBatchId ? selectedBatchId.slice(0, 12) + "..." : "None"}
                      </div>
                      {reviewCounts && (
                        <div className="text-[11px] text-muted-foreground">{reviewCounts.accepted} accepted</div>
                      )}
                    </div>
                    <div className="p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Shield className="h-3.5 w-3.5 text-purple-600" />
                        <span className="text-xs font-medium">Admin (Prod)</span>
                      </div>
                      <div className="text-[11px] font-mono truncate">
                        {siteState?.admin_snapshot_id?.slice(0, 16) || "None"}
                      </div>
                      {siteState?.admin_promoted_at && (
                        <div className="text-[11px] text-muted-foreground">
                          {formatRelativeTime(siteState.admin_promoted_at)} • {siteState.admin_deal_count} deals
                        </div>
                      )}
                    </div>
                    <div className="p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                      <div className="flex items-center gap-2 mb-1">
                        <Globe className="h-3.5 w-3.5 text-green-600" />
                        <span className="text-xs font-medium">Live (Public)</span>
                      </div>
                      <div className="text-[11px] font-mono truncate">
                        {siteState?.active_snapshot_id?.slice(0, 16) || "None"}
                      </div>
                      {siteState?.published_at && (
                        <div className="text-[11px] text-muted-foreground">
                          {formatRelativeTime(siteState.published_at)} • {siteState.published_deal_count} deals
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3 pt-2 border-t">
                    {currentMode === "staging" && (
                      <Button 
                        size="sm"
                        onClick={() => setShowPromoteDialog(true)}
                        disabled={!canPromote || promoting}
                        className="bg-purple-600 hover:bg-purple-700 h-8 text-xs"
                      >
                        {promoting ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Upload className="h-3 w-3 mr-1" />}
                        Promote {acceptedDealCount} to Admin
                      </Button>
                    )}
                    {currentMode === "admin" && (
                      <Button 
                        size="sm"
                        onClick={() => setShowPublishDialog(true)}
                        disabled={!canPublishToLive || publishing}
                        className="bg-green-600 hover:bg-green-700 h-8 text-xs"
                      >
                        {publishing ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <ArrowRight className="h-3 w-3 mr-1" />}
                        Publish to Live
                      </Button>
                    )}
                    {currentMode === "staging" && !canPromote && (
                      <span className="text-xs text-muted-foreground">
                        {!selectedBatchId ? "Select a batch run first" : acceptedDealCount === 0 ? "No accepted deals to promote" : ""}
                      </span>
                    )}
                    {currentMode === "admin" && !canPublishToLive && (
                      <span className="text-xs text-muted-foreground">
                        {!hasAdminSnapshot ? "No Admin snapshot" : !adminDifferentFromLive ? "Already live" : ""}
                      </span>
                    )}
                    {currentMode === "live" && (
                      <span className="text-xs text-muted-foreground">Read-only. Switch to Staging or Admin to make changes.</span>
                    )}
                  </div>

                  {/* Layout */}
                  <div className="pt-2 border-t">
                    <div className="flex items-center justify-between gap-4 flex-wrap">
                      <div className="flex items-center gap-3">
                        <LayoutGrid className="h-4 w-4 text-primary" />
                        <Badge variant={layoutEnvironment === "staging" ? "secondary" : "default"} className="text-xs">
                          {layoutEnvironment === "staging" ? "Staging Layout" : "Published Layout"}
                        </Badge>
                        {activeLayout.preset ? (
                          <span className="text-sm font-medium">{activeLayout.preset.name}</span>
                        ) : (
                          <span className="text-sm text-muted-foreground">No layout activated</span>
                        )}
                        {activeLayout.lastSynced && (
                          <span className="text-xs text-muted-foreground">Synced: {formatRelativeTime(activeLayout.lastSynced)}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => activeLayout.refetch()} disabled={activeLayout.loading}>
                          <RefreshCw className={`h-3 w-3 mr-1 ${activeLayout.loading ? 'animate-spin' : ''}`} />
                          Sync
                        </Button>
                        <Button variant="outline" size="sm" className="h-7 text-xs" asChild>
                          <Link to="/admin/layout-studio">
                            <LayoutGrid className="h-3 w-3 mr-1" />
                            Layout Studio
                          </Link>
                        </Button>
                      </div>
                    </div>
                    {activeLayout.preset && (
                      <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Size: <span className="font-medium text-foreground">{dealCardUXConfig.cardSize}</span></span>
                        <span>Density: <span className="font-medium text-foreground">{dealCardUXConfig.density}</span></span>
                        <span>Details: <span className="font-medium text-foreground">{dealCardUXConfig.detailsMode}</span></span>
                      </div>
                    )}
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            {/* Deal type filters */}
            <div className="mt-3">
              <DealTypeFilter
                selectedTypes={selectedDealTypes}
                onSelectionChange={setSelectedDealTypes}
                disableUnclassified={currentMode !== "staging"}
              />
            </div>

            {/* Deal Grid */}
            <div className="mt-3">
              {previewDeals.loading ? (
                <div className="text-center py-12">
                  <RefreshCw className="h-8 w-8 animate-spin mx-auto text-primary" />
                  <p className="mt-4 text-muted-foreground">Loading deals...</p>
                </div>
              ) : previewDeals.error ? (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>Error loading deals</AlertTitle>
                  <AlertDescription>{previewDeals.error}</AlertDescription>
                </Alert>
              ) : currentMode === "staging" && !selectedBatchId ? (
                <Alert>
                  <Database className="h-4 w-4" />
                  <AlertTitle>Select a batch run</AlertTitle>
                  <AlertDescription>Choose a batch run from the toolbar to preview staging deals.</AlertDescription>
                </Alert>
              ) : filteredDeals.length === 0 ? (
                <Alert>
                  <Database className="h-4 w-4" />
                  <AlertTitle>No deals available</AlertTitle>
                  <AlertDescription>
                    {currentMode === "staging" ? "No deals found in this batch run." :
                     currentMode === "live" && !hasActiveSnapshot ? "No snapshot is published." : "No deals match your filters."}
                  </AlertDescription>
                </Alert>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">
                      {filteredDeals.length} Deal{filteredDeals.length !== 1 ? "s" : ""}
                    </h2>
                  </div>
                  
                  <div className="relative">
                    <PublishedDealCardConfigProvider environment={dealCardConfigEnv}>
                      <DealCardUXProvider
                        cardSize={dealCardUXConfig.cardSize}
                        density={dealCardUXConfig.density}
                        detailsMode={dealCardUXConfig.detailsMode}
                        expandSingleOpen={dealCardUXConfig.expandSingleOpen}
                        detailsSections={dealCardUXConfig.detailsSections}
                        customHeightsEnabled={dealCardUXConfig.customHeightsEnabled}
                        customHeightsPx={dealCardUXConfig.customHeightsPx}
                        feedbackWidget={dealCardUXConfig.feedbackWidget}
                      >
                        {currentMode === "staging" ? (
                          <LivePreviewStagingGrid deals={filteredDeals} onReview={handleReview} />
                        ) : (
                          <DealGridWithDetails
                            deals={filteredDeals}
                            isAdmin={true}
                            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
                          />
                        )}
                      </DealCardUXProvider>
                    </PublishedDealCardConfigProvider>
                    <FoldLineOverlay foldHeight={foldHeight} />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ========== DIAGNOSTICS TAB ========== */}
          <TabsContent value="diagnostics" className="mt-0">
            <DiagnosticsTabContent
              siteState={siteState}
              publishHistory={publishHistory}
              currentMode={currentMode}
              dealsCount={previewDeals.deals.length}
              stateLoading={stateLoading}
              previewLoading={previewDeals.loading}
              onRefresh={handleRefresh}
              endpointUrl={endpointUrl}
              selectedBatchId={selectedBatchId}
              error={previewDeals.error}
              filteredCount={filteredDeals.length}
              batchRunsCount={previewDeals.batchRuns.length}
              batchRunsLoading={previewDeals.batchRunsLoading}
            />

            {/* Recent Publish History */}
            {recentPublishes.length > 0 && (
              <Card className="mt-4">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Recent Publish History
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {recentPublishes.map((entry) => (
                      <div key={entry.id} className="flex items-center justify-between text-sm p-2 bg-muted/50 rounded">
                        <div className="flex items-center gap-3">
                          <Badge variant={entry.action === "PUBLISH" ? "default" : "secondary"}>
                            {entry.action}
                          </Badge>
                          <span className="font-mono text-xs">{entry.snapshot_id}</span>
                          <span className="text-muted-foreground">{entry.deal_count} deals</span>
                        </div>
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <span>{entry.performed_by}</span>
                          <span>•</span>
                          <span>{formatDate(entry.performed_at)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Promote Dialog */}
        <AlertDialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Promote to Admin Snapshot?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>Promote <strong>{acceptedDealCount} accepted deals</strong> from batch <code className="text-xs bg-muted px-1 py-0.5 rounded">{selectedBatchId?.slice(0, 12)}</code> to Admin.</p>
                <p className="text-muted-foreground">Not visible to public until "Publish to Live".</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={promoting}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handlePromoteToAdmin} disabled={promoting} className="bg-purple-600 hover:bg-purple-700">
                {promoting ? "Promoting..." : "Promote to Admin"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Publish Dialog */}
        <AlertDialog open={showPublishDialog} onOpenChange={setShowPublishDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Publish to Live?</AlertDialogTitle>
              <AlertDialogDescription className="space-y-2">
                <p>Make Admin snapshot <code className="text-xs bg-muted px-1 py-0.5 rounded">{siteState?.admin_snapshot_id?.slice(0, 20)}</code> visible to the public.</p>
                <p className="font-medium text-foreground">{siteState?.admin_deal_count} deals will be shown.</p>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={publishing}>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handlePublishToLive} disabled={publishing} className="bg-green-600 hover:bg-green-700">
                {publishing ? "Publishing..." : "Publish to Live"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
